import { useState, useEffect } from 'react';

/**
 * Custom hook to detect if the user prefers reduced motion
 * This helps make animations accessible for users with vestibular disorders or motion sensitivity
 * 
 * @param overrideValue - Optional override to set the reduced motion state manually
 * @returns Whether reduced motion is preferred
 */
export function useReducedMotion(overrideValue?: boolean): boolean {
    // Use the provided override value or check the media query
    const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(() => {
        if (overrideValue !== undefined) {
            return overrideValue;
        }

        // Check if window is available (for SSR compatibility)
        if (typeof window === 'undefined') {
            return false;
        }

        // Check the media query for prefers-reduced-motion
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });

    useEffect(() => {
        // If we have an override value, don't set up listeners
        if (overrideValue !== undefined) {
            setPrefersReducedMotion(overrideValue);
            return;
        }

        // Skip in non-browser environments
        if (typeof window === 'undefined') {
            return;
        }

        // Create the media query
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        // Define the change handler
        const handleMediaChange = () => {
            setPrefersReducedMotion(mediaQuery.matches);

            // Add a data attribute to the document element for CSS usage
            if (mediaQuery.matches) {
                document.documentElement.setAttribute('data-reduced-motion', 'true');
            } else {
                document.documentElement.removeAttribute('data-reduced-motion');
            }
        };

        // Call it once to initialize
        handleMediaChange();

        // Add the listener
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMediaChange);
        } else {
            // Fallback for older browsers
            mediaQuery.addListener(handleMediaChange);
        }

        // Cleanup
        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMediaChange);
            } else {
                // Fallback for older browsers
                mediaQuery.removeListener(handleMediaChange);
            }
        };
    }, [overrideValue]);

    return prefersReducedMotion;
}

/**
 * Get animation properties based on reduced motion preference
 * 
 * @param options - Animation options with standard and reduced alternatives
 * @param isReducedMotion - Whether reduced motion is active (from useReducedMotion)
 * @returns The appropriate animation properties
 */
export function getReducedMotionAnimations<T>({
    standard,
    reduced,
}: {
    standard: T;
    reduced: T;
}, isReducedMotion: boolean): T {
    return isReducedMotion ? reduced : standard;
}

export default useReducedMotion; 